import clsx from "clsx";
import { useTranslation } from "react-i18next";
import InternalPageLayout from "../../layouts/InternalPage/InternalPageLayout";

const PrivacyPolicyPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <InternalPageLayout
      title={t("Política de Privacidad")}
      description={t("Política de Privacidad de eXpenser.net")}
    >
      <div
        className={clsx([
          "flex",
          "w-full",
          "flex-col",
          "gap-6",
          "text-sm md:text-base",
        ])}
      >
        <div className={clsx(["flex", "gap-4", "flex-col"])}>
          <h2 className={clsx(["text-lg", "md:text-xl", "font-medium"])}>
            1. Introducción
          </h2>
          <p>
            Gracias por utilizar <strong>eXpenser.net</strong>. Esta Política de
            Privacidad tiene como objetivo informarte sobre cómo recopilamos,
            usamos, protegemos y compartimos la información personal que nos
            proporcionas cuando utilizas nuestra aplicación{" "}
            <strong>eXpenser.net</strong> (en adelante, "la aplicación").
          </p>
          <p>
            <strong>eXpenser.net</strong> es una herramienta diseñada para el
            control de ingresos y gastos, que ayuda a los usuarios a gestionar
            su información financiera de manera eficiente.
          </p>
        </div>
        <div className={clsx(["flex", "gap-4", "flex-col"])}>
          <h2 className={clsx(["text-lg", "md:text-xl", "font-medium"])}>
            2. Información que recopilamos
          </h2>
          <p>
            <strong>eXpenser.net</strong> recopila los siguientes tipos de
            información:
          </p>
          <ul className="list-disc ml-8 ">
            <li>
              <strong>Datos de la cuenta:</strong> Cuando te registras o inicias
              sesión en la aplicación, recopilamos información como tu nombre,
              dirección de correo electrónico, y otra información necesaria para
              crear y administrar tu cuenta.
            </li>
            <li>
              <strong>Datos financieros:</strong> Recopilamos información
              relacionada con tus ingresos, gastos y transacciones para ayudarte
              a llevar un control de tu situación financiera. Esta información
              es ingresada directamente por ti.
            </li>
            <li>
              <strong>Datos técnicos:</strong> Cuando usas nuestra aplicación,
              podemos recopilar datos sobre tu dispositivo, como el tipo de
              navegador, sistema operativo, dirección IP, y otros datos técnicos
              relacionados con tu uso de la aplicación.
            </li>
          </ul>
        </div>

        <div className={clsx(["flex", "gap-4", "flex-col"])}>
          <h2 className={clsx(["text-lg", "md:text-xl", "font-medium"])}>
            3. Uso de la información
          </h2>
          <p>
            La información que recopilamos se utiliza para los siguientes fines:
          </p>
          <ul className="list-disc ml-8 ">
            <li>
              <strong>Proveer y mejorar nuestros servicios:</strong> Utilizamos
              tu información para proporcionarte las funcionalidades de la
              aplicación, como el control de tus ingresos y gastos.
            </li>
            <li>
              <strong>Mejorar la experiencia del usuario:</strong> Analizamos
              cómo utilizas la aplicación para mejorar la interfaz y la
              funcionalidad.
            </li>
            <li>
              <strong>Soporte al cliente:</strong> Si tienes alguna pregunta o
              solicitud, podemos utilizar tu información para responder de
              manera eficiente.
            </li>
            <li>
              <strong>Cumplir con requisitos legales y regulatorios:</strong>{" "}
              Podemos utilizar la información que recopilamos para cumplir con
              las leyes aplicables.
            </li>
          </ul>
        </div>

        <div className={clsx(["flex", "gap-4", "flex-col"])}>
          <h2 className={clsx(["text-lg", "md:text-xl", "font-medium"])}>
            4. Cómo protegemos tu información
          </h2>
          <p>
            La seguridad de tu información es muy importante para nosotros.
            Implementamos medidas de seguridad para proteger los datos
            personales que recopilamos, incluyendo cifrado de datos y políticas
            de acceso restringido. Sin embargo, ten en cuenta que ningún sistema
            es completamente seguro y no podemos garantizar la seguridad
            absoluta de los datos.
          </p>
        </div>

        <div className={clsx(["flex", "gap-4", "flex-col"])}>
          <h2 className={clsx(["text-lg", "md:text-xl", "font-medium"])}>
            5. Compartir y divulgar la información
          </h2>
          <p>
            En <strong>eXpenser.net</strong>, tu privacidad es nuestra
            prioridad. <strong>No vendemos, ni compartimos</strong> tus datos
            personales con terceros con fines comerciales.
          </p>
          <p>
            Podemos compartir tu información con terceros solo en las siguientes
            circunstancias:
          </p>
          <ul className="list-disc ml-8 ">
            <li>
              <strong>Proveedores de servicios:</strong> Trabajamos con
              proveedores de servicios que nos ayudan a operar la aplicación,
              como servicios de hosting, análisis de datos o soporte al cliente.
              Estos proveedores tienen acceso limitado a tu información y solo
              pueden utilizarla para proporcionarnos esos servicios.
            </li>
            <li>
              <strong>Cumplimiento legal:</strong> Si es necesario, podemos
              compartir tu información para cumplir con una obligación legal,
              como una orden judicial o para proteger nuestros derechos legales.
            </li>
          </ul>
        </div>

        <div className={clsx(["flex", "gap-4", "flex-col"])}>
          <h2 className={clsx(["text-lg", "md:text-xl", "font-medium"])}>
            6. Tus derechos sobre tus datos
          </h2>
          <p>Como usuario, tienes derecho a:</p>
          <ul className="list-disc ml-8 ">
            <li>
              <strong>Acceder a tus datos:</strong> Puedes solicitar información
              sobre los datos que hemos recopilado sobre ti.
            </li>
            <li>
              <strong>Modificar tus datos:</strong> Puedes actualizar o corregir
              la información que tienes en tu cuenta.
            </li>
            <li>
              <strong>Eliminar tus datos:</strong> Si decides que ya no deseas
              utilizar la aplicación, puedes solicitar que eliminemos tu cuenta
              y los datos asociados.
            </li>
            <li>
              <strong>Oponerte al tratamiento:</strong> Si tienes preocupaciones
              sobre cómo utilizamos tus datos, puedes ponerte en contacto con
              nosotros para discutirlo.
            </li>
          </ul>
          <p>
            Para ejercer estos derechos, puedes ponerte en contacto con nosotros
            a través de las siguientes formas de contacto.
          </p>
        </div>

        <div className={clsx(["flex", "gap-4", "flex-col"])}>
          <h2 className={clsx(["text-lg", "md:text-xl", "font-medium"])}>
            7. Retención de datos
          </h2>
          <p>
            Retendremos tus datos durante el tiempo que sea necesario para
            cumplir con los fines establecidos en esta política o hasta que
            elimines tu cuenta.
          </p>
        </div>

        <div className={clsx(["flex", "gap-4", "flex-col"])}>
          <h2 className={clsx(["text-lg", "md:text-xl", "font-medium"])}>
            8. Cambios en esta Política de Privacidad
          </h2>
          <p>
            Nos reservamos el derecho de modificar esta Política de Privacidad
            en cualquier momento. Te notificaremos sobre cualquier cambio
            mediante la publicación de la nueva política en nuestra aplicación.
            La fecha de la última actualización se reflejará en la parte
            superior de esta página.
          </p>
          <p>
            Te recomendamos que revises periódicamente esta Política de
            Privacidad para estar informado sobre cómo estamos protegiendo tu
            información.
          </p>
        </div>

        <div className={clsx(["flex", "gap-4", "flex-col"])}>
          <h2 className={clsx(["text-lg", "md:text-xl", "font-medium"])}>
            9. Contacto
          </h2>
          <p>
            Si tienes alguna pregunta sobre esta Política de Privacidad o sobre
            cómo gestionamos tu información, no dudes en ponerte en contacto con
            nosotros a través de:
          </p>
          <ul className="list-none ml-0 mt-4 ">
            <li>
              <strong>Correo electrónico:</strong>{" "}
              <a
                href="mailto:dev@calirojas.com"
                className="text-black dark:text-white underline"
              >
                dev@calirojas.com
              </a>
            </li>
            <li>
              <strong>Dirección:</strong> San José, Costa Rica
            </li>
          </ul>
        </div>
      </div>
    </InternalPageLayout>
  );
};

export default PrivacyPolicyPage;
